import type {
  DocumentUploadOutcomeResponse,
  UploadBatchEntry,
  UploadBatchInput,
  UploadOutcomeCode,
  UploadSessionResponse,
  UploadSessionStatus,
} from '@contract-analyzer/api-client';

interface OutcomeCopy {
  readonly label: string;
  readonly message: string;
  /** Sending the same file again can succeed; otherwise the user must act first. */
  readonly retryable: boolean;
}

/** One explanation per published outcome code; the generated enum keeps this exhaustive. */
const OUTCOME_COPY: Readonly<Record<UploadOutcomeCode, OutcomeCopy>> = {
  'archive-ingestion-failed': {
    label: 'Not processed',
    message: 'Its ZIP archive could not be processed, so this entry was not admitted. Retry the archive.',
    retryable: true,
  },
  'ingestion-retries-exhausted': {
    label: 'Processing failed',
    message: 'The document could not be processed after several attempts. Retry, or contact support if it keeps failing.',
    retryable: true,
  },
  'invalid-archive': {
    label: 'Needs replacement',
    message: 'This ZIP is damaged, holds more than 1,000 entries or expands beyond 5 GB. Re-export or split it, then upload the new archive.',
    retryable: false,
  },
  'invalid-document-content': {
    label: 'Needs replacement',
    message: 'This file is damaged or is not a readable document of its type. Export a fresh copy and upload it again.',
    retryable: false,
  },
  'malware-detected': {
    label: 'Needs replacement',
    message: 'The security scan found unsafe content. Upload a clean copy of this document.',
    retryable: false,
  },
  'project-archived': {
    label: 'Project archived',
    message: 'The project was archived before this file was processed. Restore the project, then upload the file again.',
    retryable: false,
  },
  'quota-exceeded': {
    label: 'Quota reached',
    message: 'The free document allowance is used up. Add a charge code, then retry.',
    retryable: true,
  },
  'scan-not-performed': {
    label: 'Not scanned',
    message: 'The security service could not scan this file. Contact your workspace administrator before retrying.',
    retryable: true,
  },
  'scan-result-unrecognized': {
    label: 'Not scanned',
    message: 'The security scan returned a result this service does not recognise. Contact your workspace administrator before retrying.',
    retryable: true,
  },
  // The one waiting code: the session is still owned and resumes when the scan reports.
  'scan-stalled': {
    label: 'Scan delayed',
    message: 'The security scan is taking longer than usual. It continues on the server; you can leave this page.',
    retryable: false,
  },
  'scan-timeout': {
    label: 'Scan timed out',
    message: 'The security scan did not finish in time. Retry to upload the file again.',
    retryable: true,
  },
  'source-identity-mismatch': {
    label: 'Changed during upload',
    message: 'The file changed while it was being uploaded. Retry to send its current contents.',
    retryable: true,
  },
  'workspace-unavailable': {
    label: 'Not processed',
    message: 'The service could not set aside room to process this file in time. Retry, or contact support if it keeps failing.',
    retryable: true,
  },
};

const UNKNOWN_REJECTION: OutcomeCopy = {
  label: 'Needs attention',
  message: 'The document could not be verified. Try again or contact support.',
  retryable: true,
};

/**
 * Sessions settle with a typed outcome code. Entries without one are archive
 * members refused during expansion, or uploads cancelled before processing,
 * and explain themselves in prose.
 */
function rejectionCopy(code: UploadOutcomeCode | null | undefined, reason: string | null | undefined = null): OutcomeCopy {
  if (code != null) return OUTCOME_COPY[code];
  return reason == null ? UNKNOWN_REJECTION : { label: 'Needs replacement', message: reason, retryable: false };
}
/** The part of a live Uppy file that status derivation needs. */
export interface TransferProgress {
  readonly started: boolean;
  readonly percentage: number;
}

/** How a transfer ended once its file left the transfer queue. */
export type SettledTransfer =
  | { readonly kind: 'sent'; readonly session: UploadSessionResponse }
  | { readonly kind: 'failed'; readonly message: string; readonly retryable: boolean }
  | { readonly kind: 'cancelled' };

type UploadPhase =
  | 'queued' | 'sending' | 'verifying' | 'received' | 'rejected'
  | 'interrupted' | 'failed' | 'cancelled' | 'not-received' | 'unknown';

/** One input's status, derived identically for live and restored batches. */
export interface UploadInputStatus {
  readonly phase: UploadPhase;
  readonly label: string;
  readonly message: string | null;
  /** Percentage of bytes the server has acknowledged. */
  readonly progress: number;
  readonly needsAttention: boolean;
  /** The browser still holds the file and sending it again can succeed. */
  readonly retryable: boolean;
  /** Cancelling can still stop the transfer before the server accepts it. */
  readonly cancellable: boolean;
}

export interface UploadInputSources {
  readonly transfer: TransferProgress | undefined;
  readonly settled: SettledTransfer | undefined;
  readonly receipt: UploadBatchInput | undefined;
  /** The original file is still available to this browser tab. */
  readonly resendable: boolean;
  /** The archive was already sent again as a new upload to recover its failed entries. */
  readonly resent?: boolean;
}

const NOT_RESENDABLE = 'Select the original file in a new batch to upload it again.';
const RESEND_ARCHIVE = 'Some entries were not admitted but can be retried. Retry sends the archive again as a new upload; documents it already added are kept.';

function status(phase: UploadPhase, label: string, overrides: Partial<UploadInputStatus> = {}): UploadInputStatus {
  const needsAttention = phase !== 'queued' && phase !== 'sending' && phase !== 'verifying' && phase !== 'received';
  return {
    phase, label, message: null, progress: needsAttention ? 0 : 100,
    needsAttention, retryable: false, cancellable: false, ...overrides,
  };
}

function serverStatus(
  sessionStatus: UploadSessionStatus,
  outcomeCode: UploadOutcomeCode | null | undefined,
  resendable: boolean,
): UploadInputStatus | null {
  switch (sessionStatus) {
    case 'receiving':
      return null;
    case 'ready':
    case 'processing':
      return status('verifying', 'Checking file');
    case 'stalled': {
      const { label, message } = OUTCOME_COPY['scan-stalled'];
      return status('verifying', label, { message });
    }
    case 'succeeded':
    case 'duplicate':
      return status('received', 'Received');
    case 'rejected': {
      const copy = rejectionCopy(outcomeCode);
      const retryable = copy.retryable && resendable;
      return status('rejected', copy.label, {
        message: copy.retryable && !resendable ? `${copy.message} ${NOT_RESENDABLE}` : copy.message,
        retryable,
      });
    }
    case 'cancelled':
    case 'expired':
      return status('cancelled', sessionStatus === 'cancelled' ? 'Cancelled' : 'Expired', {
        retryable: resendable, message: resendable ? null : NOT_RESENDABLE,
      });
  }
}

function settledStatus(settled: SettledTransfer, resendable: boolean): UploadInputStatus | null {
  switch (settled.kind) {
    case 'sent':
      return serverStatus(settled.session.status, settled.session.outcome_code, resendable);
    case 'failed':
      return settled.retryable
        ? status('interrupted', 'Interrupted', { message: settled.message, retryable: resendable })
        : status('failed', 'Not sent', { message: settled.message });
    case 'cancelled':
      return status('cancelled', 'Cancelled', { retryable: resendable });
  }
}

/**
 * The single status derivation, shared by live and restored batches. A live
 * transfer wins. A recorded settlement is newer than any receipt read before
 * it (the store drops it once a later read arrives); otherwise the server
 * receipt is authoritative.
 */
export function deriveInputStatus(sources: UploadInputSources): UploadInputStatus {
  const derived = inputStatus(sources);
  return derived.phase === 'received' ? withEntryRecovery(derived, sources) : derived;
}

/** A rejected entry that sending the same file again can admit, such as an archive child whose scan timed out. */
function isRecoverableEntry(entry: UploadBatchEntry): boolean {
  return entry.outcome === 'rejected' && rejectionCopy(entry.outcome_code, entry.reason).retryable;
}

/**
 * A received archive settles once every child has an outcome, including children
 * rejected for reasons a fresh attempt can overcome. The archive's own session
 * succeeded and cannot be replaced within its input, so recovery sends the archive
 * again as a new upload: documents it already admitted deduplicate and are kept.
 */
function withEntryRecovery(received: UploadInputStatus, { receipt, resendable, resent }: UploadInputSources): UploadInputStatus {
  if (receipt === undefined || !receipt.entries.some(isRecoverableEntry)) return received;
  if (resent === true) return { ...received, message: 'Sent again as a new upload to recover the entries that failed.' };
  return {
    ...received, label: 'Some entries failed', needsAttention: true, retryable: resendable,
    message: resendable ? RESEND_ARCHIVE : `Some entries were not admitted. ${NOT_RESENDABLE}`,
  };
}

function inputStatus({ transfer, settled, receipt, resendable }: UploadInputSources): UploadInputStatus {
  if (transfer !== undefined) {
    return transfer.started
      ? status('sending', `Uploading ${String(transfer.percentage)}%`, { progress: transfer.percentage, cancellable: true })
      : status('queued', 'Queued', { progress: 0, cancellable: true });
  }
  const fromSettlement = settled === undefined ? null : settledStatus(settled, resendable);
  if (fromSettlement !== null) return fromSettlement;
  const fromReceipt = receipt?.session_status == null ? null
    : serverStatus(receipt.session_status, receipt.outcome_code, resendable);
  if (fromReceipt !== null) return fromReceipt;
  if (receipt === undefined) return status('unknown', 'Receipt unavailable', {
    message: 'Refresh the receipt to check whether this file was received.',
  });
  return resendable
    ? status('cancelled', 'Not sent', { retryable: true })
    : status('not-received', 'Not received', { message: NOT_RESENDABLE });
}

type EntryStage = 'attention' | 'processing' | 'ready';

/** One admitted, skipped or rejected document entry of an input. */
export interface EntryStatus {
  readonly stage: EntryStage;
  readonly label: string;
  readonly reason: string | null;
}

function documentStatus(outcome: Extract<DocumentUploadOutcomeResponse, { outcome: 'live' }>): EntryStatus {
  const { document } = outcome;
  if (document.failure_reason != null || document.ocr_status === 'failed' || document.embedding_status === 'failed') {
    return { stage: 'attention', label: 'Processing failed',
      reason: document.failure_reason ?? 'Open the document to review processing and retry.' };
  }
  if (document.ocr_status === 'processed' && document.embedding_status === 'processed') {
    return { stage: 'ready', label: 'Ready', reason: null };
  }
  return { stage: 'processing', label: document.ocr_status === 'processed' ? 'Preparing for analysis' : 'Reading document', reason: null };
}

/** Readiness of a document entry; `outcome` is its latest polled document status. */
export function deriveEntryStatus(entry: UploadBatchEntry, outcome: DocumentUploadOutcomeResponse | undefined): EntryStatus {
  if (entry.outcome === 'skipped') return { stage: 'attention', label: 'Skipped', reason: entry.reason ?? null };
  if (entry.outcome === 'rejected') {
    const copy = rejectionCopy(entry.outcome_code, entry.reason);
    return { stage: 'attention', label: copy.label, reason: copy.message };
  }
  if (entry.document_id == null || outcome?.outcome === 'unavailable') {
    return { stage: 'attention', label: 'Unavailable', reason: 'This document is no longer available in the project.' };
  }
  if (outcome?.outcome === 'rejected') return { stage: 'attention', label: 'Rejected', reason: outcome.reason_message };
  return outcome === undefined ? { stage: 'processing', label: 'Reading document', reason: null } : documentStatus(outcome);
}

/** An entry stops being polled once its document can no longer change stage. */
export function isSettledDocumentOutcome(outcome: DocumentUploadOutcomeResponse): boolean {
  return outcome.outcome !== 'live' || documentStatus(outcome).stage !== 'processing';
}
